import { Box, Button, Typography, CircularProgress, Tooltip } from "@mui/material";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import { isPlatform } from "@ionic/react";
import { Capacitor } from "@capacitor/core";
import { useEffect, useState } from "react";
import { frontEnvNumber, subscribeFrontRuntimeConfig } from "src/services/runtimeFrontConfig";
import { isDevFrontendEnv } from "src/config/frontendUrls";

interface BoosterPacksProps {
  boosterPacks: any[];
  handleOptionClick: (creditsAmount: number, subscription: string, optionKey: string, productId: string) => void;
  hoveredBoosterPack: number | null;
  setHoveredBoosterPack: (index: number | null) => void;
  processingOption: string | null;
}

const BoosterPacks = ({ boosterPacks, handleOptionClick, hoveredBoosterPack, setHoveredBoosterPack, processingOption }: BoosterPacksProps) => {
  const [, setFrontRuntimeBump] = useState(0);
  useEffect(() => subscribeFrontRuntimeConfig(() => setFrontRuntimeBump((n) => n + 1)), []);
  const powerBoosterCredits = frontEnvNumber("REACT_APP_BOOSTER_POWER_CREDITS", 250);

  const paymentsDisabledInCurrentEnv = isDevFrontendEnv();
  // const paymentsDisabledInCurrentEnv = false; // TEMP: enable dev payments test

  const paymentsDisabledTooltip = "Payments are disabled in dev. Use prod to complete purchases.";
  const [openedTooltipKey, setOpenedTooltipKey] = useState<string | null>(null);
  const isNativePlatform = Capacitor.isNativePlatform();
  const isHybridPlatform = isPlatform("hybrid");
  const isRunningNatively = isNativePlatform || isHybridPlatform;
  return (
    <Box sx={{ mt: 2 }}>
      {/* Info Banner */}
      <Box
        sx={{
          bgcolor: "#1976d2",
          color: "#FFFFFF",
          p: 1.5,
          borderRadius: 1,
          mb: 2,
          textAlign: "center",
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            color: "#FFFFFF",
          }}
        >
          One time purchase. No subscription necessary.
        </Typography>
      </Box>

      {/* Booster Packs */}
      {boosterPacks.map((pack, index) => {
        // Check if this is one of the new boosters (has name field)
        const isNewBooster = pack.name !== undefined;

        if (isNewBooster) {
          // New layout for STARTER BOOSTER and POWER BOOSTER
          return (
            <Box
              key={index}
              onMouseEnter={() => setHoveredBoosterPack(index)}
              onMouseLeave={() => setHoveredBoosterPack(null)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: "#151921",
                border: hoveredBoosterPack === index ? `2px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}` : "2px solid rgba(255, 255, 255, 0.2)",
                borderRadius: 2,
                p: 2,
                mb: 2,
                transition: "border-color 0.2s ease",
              }}
            >
              {/* Left side: Title, Price, Credits */}
              <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                {/* Title */}
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "20px",
                    fontWeight: "bold",
                    color: pack.productId === "league_connect_boost_power" ? `${PureLightThemeCaaS.colors.alpha.orange[100]}` : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    mb: 0,
                    textTransform: "uppercase",
                  }}
                >
                  {pack.name}
                </Typography>

                {/* Price (large) */}
                <Typography
                  variant="h5"
                  sx={{
                    fontSize: "24px",
                    fontWeight: "bold",
                    color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    mb: 0.5,
                  }}
                >
                  {pack.price}
                </Typography>

                {/* Credits */}
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: pack.productId === "league_connect_boost_power" ? `${PureLightThemeCaaS.colors.alpha.orange[100]}` : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    mb: 1,
                    textTransform: "uppercase",
                  }}
                >
                  {pack.creditsNumber} CREDITS
                </Typography>

                {/* Credits with price per credit */}
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    px: 1,
                    py: 0,
                    borderRadius: 0.5,
                    fontSize: "10px",
                    fontWeight: "bold",
                    border: "1px solid",
                    bgcolor: pack.productId === "league_connect_boost_power" ? "rgba(255, 152, 0, 0.1)" : "transparent",
                    borderColor:
                      pack.productId === "league_connect_boost_power" ? `${PureLightThemeCaaS.colors.alpha.orange[100]}` : `${PureLightThemeCaaS.colors.alpha.white[30]}`,
                    color: pack.productId === "league_connect_boost_power" ? `${PureLightThemeCaaS.colors.alpha.orange[100]}` : `${PureLightThemeCaaS.colors.alpha.white[70]}`,
                    width: "fit-content",
                  }}
                >
                  {pack.pricePerCredit}
                </Box>
              </Box>

              {/* Right side: Button */}
              <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                <Tooltip
                  title={paymentsDisabledInCurrentEnv ? paymentsDisabledTooltip : ""}
                  placement="top"
                  arrow
                  open={openedTooltipKey === `pack-${index}`}
                  disableHoverListener
                  disableFocusListener
                  disableTouchListener
                  componentsProps={{
                    tooltip: {
                      sx: {
                        textAlign: "center",
                        width: "90%",
                        margin: "0 auto",
                      },
                    },
                  }}
                >
                  <span
                    onClick={() => {
                      if (!paymentsDisabledInCurrentEnv) return;
                      setOpenedTooltipKey(`pack-${index}`);
                      window.setTimeout(() => setOpenedTooltipKey((current) => (current === `pack-${index}` ? null : current)), 2500);
                    }}
                    onTouchStart={() => {
                      if (!paymentsDisabledInCurrentEnv) return;
                      setOpenedTooltipKey(`pack-${index}`);
                      window.setTimeout(() => setOpenedTooltipKey((current) => (current === `pack-${index}` ? null : current)), 2500);
                    }}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        handleOptionClick(pack.creditsNumber, pack.subscription, `pack-${index}`, pack.productId);
                      }}
                      disabled={!!processingOption || paymentsDisabledInCurrentEnv}
                      sx={{
                        borderColor: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        borderRadius: 1,
                        px: 1,
                        textTransform: "none",
                        minWidth: 80,
                        "&:hover": {
                          borderColor: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                          bgcolor: "rgba(255, 255, 255, 0.05)",
                        },
                        "&.Mui-disabled": {
                          borderColor: `${PureLightThemeCaaS.colors.alpha.white[30]}`,
                          color: `${PureLightThemeCaaS.colors.alpha.white[30]}`,
                        },
                      }}
                    >
                      {processingOption === `pack-${index}` ? (
                        <CircularProgress size={25} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} />
                      ) : (
                        pack.buttonText || "BUY NOW"
                      )}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          );
        } else {
          // Original layout for existing boosters
          return (
            <Box
              key={index}
              onMouseEnter={() => setHoveredBoosterPack(index)}
              onMouseLeave={() => setHoveredBoosterPack(null)}
              sx={{
                display: "flex",
                alignItems: "center",
                bgcolor: "#151921",
                border: hoveredBoosterPack === index ? `1px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}` : "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: 2,
                p: 2,
                mb: 2,
                gap: 2,
                transition: "border-color 0.2s ease",
              }}
            >
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  bgcolor: pack.creditsNumber === powerBoosterCredits ? `${PureLightThemeCaaS.colors.alpha.orange[100]}` : "rgba(255, 255, 255, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: pack.creditsNumber === powerBoosterCredits ? "#FFFFFF" : `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                  }}
                >
                  {pack.creditsNumber}
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontSize: "18px",
                    fontWeight: "bold",
                    color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                    mb: 0.5,
                  }}
                >
                  {pack.credits}
                </Typography>
                {pack.pricePerCredit ? (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      color: "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    {pack.pricePerCredit}
                  </Typography>
                ) : (
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: "14px",
                      color: "rgba(255, 255, 255, 0.7)",
                    }}
                  >
                    Booster Pack
                  </Typography>
                )}
              </Box>

              <Tooltip
                title={paymentsDisabledInCurrentEnv ? paymentsDisabledTooltip : ""}
                placement="top"
                arrow
                open={openedTooltipKey === `pack-${index}`}
                disableHoverListener
                disableFocusListener
                disableTouchListener
                componentsProps={{
                  tooltip: {
                    sx: {
                      textAlign: "center",
                      width: "90%",
                      margin: "0 auto",
                    },
                  },
                }}
              >
                <span
                  onClick={() => {
                    if (!paymentsDisabledInCurrentEnv) return;
                    setOpenedTooltipKey(`pack-${index}`);
                    window.setTimeout(() => setOpenedTooltipKey((current) => (current === `pack-${index}` ? null : current)), 2500);
                  }}
                  onTouchStart={() => {
                    if (!paymentsDisabledInCurrentEnv) return;
                    setOpenedTooltipKey(`pack-${index}`);
                    window.setTimeout(() => setOpenedTooltipKey((current) => (current === `pack-${index}` ? null : current)), 2500);
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      handleOptionClick(pack.creditsNumber, pack.subscription, `pack-${index}`, pack.productId);
                    }}
                    disabled={!!processingOption || paymentsDisabledInCurrentEnv}
                    sx={{
                      borderColor: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                      color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                      borderRadius: 2,
                      px: 1,
                      textTransform: "none",
                      minWidth: 80,
                      "&:hover": {
                        borderColor: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                        bgcolor: "rgba(255, 255, 255, 0.05)",
                      },
                      "&.Mui-disabled": {
                        borderColor: `${PureLightThemeCaaS.colors.alpha.white[30]}`,
                        color: `${PureLightThemeCaaS.colors.alpha.white[30]}`,
                      },
                    }}
                  >
                    {processingOption === `pack-${index}` ? <CircularProgress size={25} sx={{ color: `${PureLightThemeCaaS.colors.alpha.orange[100]}` }} /> : pack.price}
                  </Button>
                </span>
              </Tooltip>
            </Box>
          );
        }
      })}
    </Box>
  );
};

export default BoosterPacks;
